/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import { Patch } from 'immer';

interface GetScholarshipsParams {
  page?: number;
  limit?: number;
  search?: string;
  scholarship_type?: string;
  university_id?: string;
  status?: string;
  amountMin?: number | null;
  amountMax?: number | null;
}

export const scholarshipsApi = createApi({
  reducerPath: 'scholarshipsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Scholarships', 'FavoritesScholarships'],
  endpoints: (builder) => ({
    getScholarships: builder.query({
      query: (params: GetScholarshipsParams = {}) => {
        const { page = 1, limit, ...filters } = params;
        const queryParts = [`page=${page.toString()}`];

        if (limit) {
          queryParts.push(`limit=${limit.toString()}`);
        }

        (
          Object.entries(filters) as [keyof GetScholarshipsParams, string][]
        ).forEach(([key, value]) => {
          if (value) {
            queryParts.push(`${key}=${encodeURIComponent(value)}`);
          }
        });

        return {
          url: `scholarships?${queryParts.join('&')}`,
          method: 'GET'
        };
      },
      providesTags: ['Scholarships']
    }),
    getScholarshipById: builder.query({
      query: (scholarshipId: string) => `scholarships/${scholarshipId}`
    }),
    applyForScholarship: builder.mutation({
      query: (applicationData) => ({
        url: 'student-scholarships',
        method: 'POST',
        body: applicationData
      })
    }),
    getUserScholarshipApplications: builder.query({
      query: (userId) =>
        `/student-scholarships?page=1&student_id=${userId}&limit=30`
    }),
    getFavoriteScholarships: builder.query({
      query: ({ page = 1, limit = 10 }) => ({
        url: `scholarships/user/favorites?page=${page}&limit=${limit}`,
        method: 'GET'
      }),
      serializeQueryArgs: () => {
        // Use a consistent cache key for all pages
        return 'favoriteScholarships';
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          // First page, replace the cache
          return newItems;
        }
        // Subsequent pages, append to existing cache
        if (currentCache?.data && newItems?.data) {
          return {
            ...newItems,
            data: [...currentCache.data, ...newItems.data]
          };
        }
        return newItems;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        // Force refetch if page changes
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ['FavoritesScholarships']
    }),
    addScholarshipToFavorite: builder.mutation({
      query: (scholarship_id) => ({
        url: `scholarships/${scholarship_id}/favorites`,
        method: 'POST'
      }),
      invalidatesTags: ['FavoritesScholarships'],
      async onQueryStarted(
        scholarship_id,
        { dispatch, queryFulfilled, getState }
      ) {
        // Optimistic update for getScholarships - update all cache entries
        const patchResults: {
          patches: Patch[];
          inversePatches: Patch[];
          undo: () => void;
        }[] = [];
        const cacheEntries = (getState() as any).scholarshipsApi?.queries;

        if (cacheEntries) {
          Object.keys(cacheEntries).forEach((queryKey) => {
            if (queryKey.startsWith('getScholarships')) {
              const cacheEntry = cacheEntries[queryKey];
              if (cacheEntry?.data) {
                const patchResult = dispatch(
                  scholarshipsApi.util.updateQueryData(
                    'getScholarships',
                    cacheEntry.originalArgs,
                    (draft) => {
                      if (draft?.data) {
                        const scholarship = draft.data.find(
                          (s: any) => s._id === scholarship_id
                        );
                        if (scholarship) {
                          if (!scholarship.favouriteBy) {
                            scholarship.favouriteBy = [];
                          }
                          const userId = (getState() as any).auth?.user?._id;
                          if (
                            userId &&
                            !scholarship.favouriteBy.includes(userId)
                          ) {
                            scholarship.favouriteBy.push(userId);
                          }
                        }
                      }
                    }
                  )
                );
                patchResults.push(patchResult);
              }
            }
          });
        }

        // Optimistic update for getFavoriteScholarships
        const patchResult2 = dispatch(
          scholarshipsApi.util.updateQueryData(
            'getFavoriteScholarships',
            undefined,
            (draft) => {
              if (draft?.data) {
                // Add the scholarship to favorites list if not already present
                const existingScholarship = draft.data.find(
                  (s: any) => s._id === scholarship_id
                );
                if (!existingScholarship) {
                  // Get the scholarship data from getScholarships cache
                  const scholarshipsCache = (getState() as any).scholarshipsApi
                    ?.queries;
                  const scholarshipData = scholarshipsCache?.data?.find(
                    (s: any) => s._id === scholarship_id
                  );
                  if (scholarshipData) {
                    draft.data.push(scholarshipData);
                  }
                }
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert on error
          patchResults.forEach((patchResult) => patchResult.undo());
          patchResult2.undo();
        }
      }
    }),
    removeScholarshipFromFavorite: builder.mutation({
      query: (scholarship_id) => ({
        url: `scholarships/${scholarship_id}/favorites`,
        method: 'DELETE'
      }),
      invalidatesTags: ['FavoritesScholarships'],
      async onQueryStarted(
        scholarship_id,
        { dispatch, queryFulfilled, getState }
      ) {
        // Optimistic update for getScholarships - update all cache entries
        const patchResults: {
          patches: Patch[];
          inversePatches: Patch[];
          undo: () => void;
        }[] = [];
        const cacheEntries = (getState() as any).scholarshipsApi?.queries;

        if (cacheEntries) {
          Object.keys(cacheEntries).forEach((queryKey) => {
            if (queryKey.startsWith('getScholarships')) {
              const cacheEntry = cacheEntries[queryKey];
              if (cacheEntry?.data) {
                const patchResult = dispatch(
                  scholarshipsApi.util.updateQueryData(
                    'getScholarships',
                    cacheEntry.originalArgs,
                    (draft) => {
                      if (draft?.data) {
                        const scholarship = draft.data.find(
                          (s: any) => s._id === scholarship_id
                        );
                        if (scholarship?.favouriteBy) {
                          const userId = (getState() as any).auth?.user?._id;
                          if (userId) {
                            scholarship.favouriteBy =
                              scholarship.favouriteBy.filter(
                                (id: string) => id !== userId
                              );
                          }
                        }
                      }
                    }
                  )
                );
                patchResults.push(patchResult);
              }
            }
          });
        }

        // Optimistic update for getFavoriteScholarships
        const patchResult2 = dispatch(
          scholarshipsApi.util.updateQueryData(
            'getFavoriteScholarships',
            undefined,
            (draft) => {
              if (draft?.data) {
                // Remove the scholarship from favorites list
                draft.data = draft.data.filter(
                  (s: any) => s._id !== scholarship_id
                );
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert on error
          patchResults.forEach((patchResult) => patchResult.undo());
          patchResult2.undo();
        }
      }
    })
  })
});

export const {
  useGetScholarshipsQuery,
  useGetScholarshipByIdQuery,
  useApplyForScholarshipMutation,
  useGetUserScholarshipApplicationsQuery,
  useGetFavoriteScholarshipsQuery,
  useAddScholarshipToFavoriteMutation,
  useRemoveScholarshipFromFavoriteMutation
} = scholarshipsApi;
